import { message, Row } from 'antd';
import { debounce } from 'lodash';
import moment from 'moment';
import { useEffect, useState } from 'react';

import {
  Form,
  Collapse,
  ModalBlockNavigation,
  Title,
  FormItemDatePicker,
  FormItemSelect,
} from '../../../components/index';
import { useCollapsePanels } from '../../../hooks';
import { useGetCompaniesQuery } from '../../../services/companyApi';
import {
  getCompanyDisplayName,
  formatDockingToSave,
} from '../../../utils/formatters';
import { useUpdateDockingMutation } from '../../../services/dockingApi';
import { Docking as DockingType, Vessel } from '../../../types';
import {
  createDateString,
  isNullOrUndefined,
  createDateStringPtBr,
} from '../../../utils/utils';
import { ActualTimeOfArrivalModal } from './ActualTimeOfArrivalModal';
import { DockingFormHeader } from '../dockingFormHeader/DockingFormHeader';

const { Panel } = Collapse;

type DockingPilotExpectFormProps = {
  name: string;
  selectedDocking: DockingType | undefined;
  setSelectedDocking: (value: any) => void;
  selectedStopover: any;
  currentDockingKey: number;
  setCurrentDockingKey: (value: any) => void;
  isVisibleForm: boolean;
  setIsVisibleForm: (value: boolean) => void;
  onBack?: () => void;
  formIsChanged: boolean;
  setIsFormChanged: (value: boolean) => void;
  setBlockNavigate: (value: boolean) => void;
  blockNavigate: boolean;
};

const collapsePanels = ['generalData'];

export function DockingPilotExpectForm(props: DockingPilotExpectFormProps) {
  const {
    name,
    selectedDocking,
    setSelectedDocking,
    selectedStopover,
    currentDockingKey,
    setCurrentDockingKey,
    isVisibleForm,
    setIsVisibleForm,
    onBack,
    formIsChanged,
    setIsFormChanged,
    setBlockNavigate,
    blockNavigate,
  } = props;

  const [form] = Form.useForm();

  const { onChangeSwitch, openCollapsePanels } =
    useCollapsePanels(collapsePanels);

  const [updateDocking, { isLoading: isLoadingUpdateDocking }] =
    useUpdateDockingMutation();

  const [companyPilotageName, setCompanyPilotageName] = useState('');
  const [companyTowingName, setCompanyTowingName] = useState('');
  const [companyMooringName, setCompanyMooringName] = useState('');

  const {
    data: companiesPilotageData,
    isLoading: isLoadingCompaniesPilotageData,
  } = useGetCompaniesQuery({
    page_size: 10,
    company_type: 'PILOTAGE',
    name_or_cnpj: companyPilotageName,
  });

  const { data: companiesTowingData, isLoading: isLoadingCompaniesTowingData } =
    useGetCompaniesQuery({
      page_size: 10,
      company_type: 'TOWING',
      name_or_cnpj: companyTowingName,
    });

  const {
    data: companiesMooringData,
    isLoading: isLoadingCompaniesMooringData,
  } = useGetCompaniesQuery({
    page_size: 10,
    company_type: 'MOORING',
    name_or_cnpj: companyMooringName,
  });

  function onChangePilotOnBoardExpectedBerthing(
    selectedDate: any,
    manoeuvre: 'DOCKING' | 'UNDOCKING'
  ) {
    if (selectedDate) {
      if (manoeuvre === 'DOCKING') {
        form.setFieldsValue({
          pilot_expected_leaving_on_board: moment(selectedDate).add({
            minutes: 15,
          }),
          pilot_expected_on_board: moment(selectedDate).subtract(1, 'hour'),
        });
      } else if (manoeuvre === 'UNDOCKING') {
        form.setFieldsValue({
          pilot_expected_leaving_on_board_undocking: moment(selectedDate).add({
            minutes: 15,
          }),
          pilot_expected_on_board_undocking: moment(selectedDate).subtract(
            1,
            'hour'
          ),
        });
      }
    }
  }

  function onChangePilotOnBoard(
    selectedDate: any,
    manoeuvre: 'DOCKING' | 'UNDOCKING'
  ) {
    if (selectedDate) {
      if (manoeuvre === 'DOCKING') {
        form.setFieldsValue({
          pilot_expected_leaving_on_board: moment(selectedDate).add({
            hours: 1,
            minutes: 15,
          }),
          expected_berthing: moment(selectedDate).add(1, 'hour'),
        });
      } else if (manoeuvre === 'UNDOCKING') {
        form.setFieldsValue({
          pilot_expected_leaving_on_board_undocking: moment(selectedDate).add({
            hours: 1,
            minutes: 15,
          }),
          expected_unberthing: moment(selectedDate).add(1, 'hour'),
        });
      }
    }
  }

  function formatDockingUserTracking(
    last_user: boolean | undefined,
    last_date: string | undefined
  ): string {
    if (isNullOrUndefined(last_date)) {
      return '';
    }
    return `Atualizado ${
      last_user ? 'por Suape' : 'pelo Agente'
    } em ${createDateStringPtBr(last_date)}`;
  }

  function handleFailedForm() {
    const errorFields = form
      .getFieldsError()
      .filter((field) => field.errors.length > 0);

    if (errorFields.length > 0) {
      form.getFieldInstance(errorFields[0].name).focus();
    }
  }

  function validateDocking(docking: DockingType): boolean {
    if (
      docking.expected_berthing &&
      docking.expected_unberthing &&
      !docking.docking_place?.tag
    ) {
      // Só é permitido salvar previsão de estadia se informar o berço
      message.error(
        'Não é possível informar a previsão de estadia sem informar o berço.'
      );
      return false;
    }

    if (
      docking.expected_berthing &&
      docking.expected_unberthing &&
      moment(docking.expected_berthing) > moment(docking.expected_unberthing)
    ) {
      message.error(
        'A data de previsão da atracação não pode ser maior que a data de previsão de desatracação',
        15
      );
      return false;
    }

    if (
      docking.pilot_expected_on_board &&
      docking.pilot_expected_leaving_on_board &&
      moment(docking.pilot_expected_on_board) >
        moment(docking.pilot_expected_leaving_on_board)
    ) {
      message.error(
        'A data de previsão da prático a bordo não pode ser maior que a data de previsão de saída do prático a bordo',
        15
      );
      return false;
    }

    return true;
  }

  async function handleFormSubmit(values: any) {
    if (!validateDocking({ ...selectedDocking, ...values })) return;

    if (currentDockingKey !== null) {
      const docking = {
        ...selectedDocking,
        ...values,
        docking_manoeuvre: {
          ...selectedDocking?.docking_manoeuvre,
          ...values.docking_manoeuvre,
        },
        undocking_manoeuvre: {
          ...selectedDocking?.undocking_manoeuvre,
          ...values.undocking_manoeuvre,
        },
      };
      const update = formatDockingToSave(docking);
      const updatedDocking = await updateDocking(update);
      if ('data' in updatedDocking) {
        message.success('Alterações salvas com sucesso!', 5);
      }
    }

    setSelectedDocking(undefined);
    setCurrentDockingKey(null);
    setIsVisibleForm(false);
    setBlockNavigate(false);
    setIsFormChanged(false);

    form.resetFields();
  }

  useEffect(() => {
    if (isVisibleForm === false) {
      setSelectedDocking(undefined);
      setCurrentDockingKey(null);
      form.resetFields();
    }
  }, [isVisibleForm]);

  function panelHeaderTitle(title: string) {
    const date = form.getFieldValue('updated_at');

    return (
      <>
        {title}
        {!isNullOrUndefined(date) && (
          <span
            className="header-description"
            style={{ float: 'right', fontWeight: 'normal', fontSize: '13px' }}
          >
            Atualizado em: {moment(date).format('DD/MM/YYYY HH:mm')}
          </span>
        )}
      </>
    );
  }

  function onClose() {
    if (formIsChanged) {
      setBlockNavigate(true);
    } else {
      setBlockNavigate(false);
      if (onBack) {
        onBack();
      }
      afterCloseModal();
      setIsVisibleForm(false);
    }
  }

  function afterCloseModal() {
    setSelectedDocking(undefined);
    setCurrentDockingKey(null);
    form.resetFields();
  }

  return (
    <Form
      form={form}
      autoComplete="off"
      layout="vertical"
      name={name}
      id={name}
      onFinish={handleFormSubmit}
      onFinishFailed={handleFailedForm}
      initialValues={selectedDocking}
      onValuesChange={() => setIsFormChanged(true)}
    >
      <ModalBlockNavigation
        name={name}
        key={name}
        setHasEdited={setIsFormChanged}
        setShowModal={setIsVisibleForm}
        showModal={blockNavigate}
        setBlockNavigate={setBlockNavigate}
        afterCloseModal={afterCloseModal}
      />

      <DockingFormHeader
        formName={name}
        isLoading={isLoadingUpdateDocking}
        onBack={onClose}
        dockingRelease={selectedStopover.docking_release}
        showExpandAll
        onChangeSwitch={onChangeSwitch}
        openCollapsePanels={openCollapsePanels}
        editFormTitle={`Atracação ${currentDockingKey + 1}`}
        code={selectedStopover?.code}
        dockingCode={form.getFieldValue('code')}
        dockingStatus={form.getFieldValue('status')}
        saveButtonTitle="Salvar Atracação"
        showCancelButton
      />
      <Collapse expandIconPosition="end" activeKey={openCollapsePanels}>
        <Panel
          forceRender
          header={panelHeaderTitle('Dados gerais')}
          key="generalData"
        >
          <div style={{ width: '100%' }}>
            <Title>ATRACAÇÃO</Title>
            <Row gutter={24} align="top">
              <FormItemDatePicker
                colSpan={8}
                label="Previsão de prático a bordo"
                name="pilot_expected_on_board"
                minuteStep={15}
                onChange={(date) => onChangePilotOnBoard(date, 'DOCKING')}
                help={formatDockingUserTracking(
                  selectedDocking?.docking_user_tracking
                    ?.pilot_expected_on_board_last_user,
                  selectedDocking?.docking_user_tracking
                    ?.pilot_expected_on_board_last_date
                )}
              />
              <FormItemDatePicker
                colSpan={8}
                label="Previsão de saída do prático a bordo"
                name="pilot_expected_leaving_on_board"
                minuteStep={15}
                help={formatDockingUserTracking(
                  selectedDocking?.docking_user_tracking
                    ?.pilot_expected_leaving_on_board_last_user,
                  selectedDocking?.docking_user_tracking
                    ?.pilot_expected_leaving_on_board_last_date
                )}
              />
              <FormItemDatePicker
                colSpan={8}
                label="Previsão de atracação"
                name="expected_berthing"
                minuteStep={15}
                tooltip="Início da atracação"
                onChange={(date) =>
                  onChangePilotOnBoardExpectedBerthing(date, 'DOCKING')
                }
                help={formatDockingUserTracking(
                  selectedDocking?.docking_user_tracking
                    ?.expected_berthing_last_user,
                  selectedDocking?.docking_user_tracking
                    ?.expected_berthing_last_date
                )}
              />
            </Row>
            <Row gutter={24} align="top">
              <FormItemSelect
                label="Empresa de amarração"
                name={['docking_manoeuvre', 'mooring_company', 'id']}
                colSpan={12}
                allowClear
                showSearch
                onSearch={debounce(
                  (e: string) => setCompanyMooringName(e),
                  400
                )}
                dataList={companiesMooringData?.results
                  .slice()
                  .map((company) => ({
                    ...company,
                    name: getCompanyDisplayName(company),
                  }))
                  .sort((a, b) => a.name.localeCompare(b.name))}
                isLoading={isLoadingCompaniesMooringData}
              />
              <FormItemSelect
                label="Empresa de rebocagem"
                name={['docking_manoeuvre', 'towage_company', 'id']}
                colSpan={12}
                allowClear
                showSearch
                onSearch={debounce((e: string) => setCompanyTowingName(e), 400)}
                dataList={companiesTowingData?.results
                  .slice()
                  .map((company) => ({
                    ...company,
                    name: getCompanyDisplayName(company),
                  }))
                  .sort((a, b) => a.name.localeCompare(b.name))}
                isLoading={isLoadingCompaniesTowingData}
              />
            </Row>
            <Row gutter={24} align="top">
              <FormItemSelect
                label="Empresa de praticagem"
                name={['docking_manoeuvre', 'pilotage_company', 'id']}
                colSpan={12}
                allowClear
                showSearch
                onSearch={debounce(
                  (e: string) => setCompanyPilotageName(e),
                  400
                )}
                dataList={companiesPilotageData?.results
                  .slice()
                  .map((company) => ({
                    ...company,
                    name: getCompanyDisplayName(company),
                  }))
                  .sort((a, b) => a.name.localeCompare(b.name))}
                isLoading={isLoadingCompaniesPilotageData}
              />
            </Row>

            <Title>DESATRACAÇÃO</Title>
            <Row gutter={24} align="top">
              <FormItemDatePicker
                colSpan={8}
                label="Previsão de prático a bordo"
                name="pilot_expected_on_board_undocking"
                minuteStep={15}
                onChange={(date) => onChangePilotOnBoard(date, 'UNDOCKING')}
                help={formatDockingUserTracking(
                  selectedDocking?.docking_user_tracking
                    ?.pilot_expected_on_board_undocking_last_user,
                  selectedDocking?.docking_user_tracking
                    ?.pilot_expected_on_board_undocking_last_date
                )}
              />
              <FormItemDatePicker
                colSpan={8}
                label="Previsão de saída do prático a bordo"
                name="pilot_expected_leaving_on_board_undocking"
                minuteStep={15}
                help={formatDockingUserTracking(
                  selectedDocking?.docking_user_tracking
                    ?.pilot_expected_leaving_on_board_undocking_last_user,
                  selectedDocking?.docking_user_tracking
                    ?.pilot_expected_leaving_on_board_undocking_last_date
                )}
              />
              <FormItemDatePicker
                colSpan={8}
                label="Previsão de desatracação"
                name="expected_unberthing"
                minuteStep={15}
                tooltip="Fim da atracação"
                onChange={(date) =>
                  onChangePilotOnBoardExpectedBerthing(date, 'UNDOCKING')
                }
                help={formatDockingUserTracking(
                  selectedDocking?.docking_user_tracking
                    ?.expected_unberthing_last_user,
                  selectedDocking?.docking_user_tracking
                    ?.expected_unberthing_last_date
                )}
              />
            </Row>
            <Row gutter={24} align="top">
              <FormItemSelect
                label="Empresa de desamarração"
                name={['undocking_manoeuvre', 'mooring_company', 'id']}
                colSpan={12}
                allowClear
                showSearch
                onSearch={debounce(
                  (e: string) => setCompanyMooringName(e),
                  400
                )}
                dataList={companiesMooringData?.results
                  .slice()
                  .map((company) => ({
                    ...company,
                    name: getCompanyDisplayName(company),
                  }))
                  .sort((a, b) => a.name.localeCompare(b.name))}
                isLoading={isLoadingCompaniesMooringData}
              />
              <FormItemSelect
                label="Empresa de rebocagem"
                name={['undocking_manoeuvre', 'towage_company', 'id']}
                colSpan={12}
                allowClear
                showSearch
                onSearch={debounce((e: string) => setCompanyTowingName(e), 400)}
                dataList={companiesTowingData?.results
                  .slice()
                  .map((company) => ({
                    ...company,
                    name: getCompanyDisplayName(company),
                  }))
                  .sort((a, b) => a.name.localeCompare(b.name))}
                isLoading={isLoadingCompaniesTowingData}
              />
            </Row>
            <Row gutter={24} align="top">
              <FormItemSelect
                label="Empresa de praticagem"
                name={['undocking_manoeuvre', 'pilotage_company', 'id']}
                colSpan={12}
                allowClear
                showSearch
                onSearch={debounce(
                  (e: string) => setCompanyPilotageName(e),
                  400
                )}
                dataList={companiesPilotageData?.results
                  .slice()
                  .map((company) => ({
                    ...company,
                    name: getCompanyDisplayName(company),
                  }))
                  .sort((a, b) => a.name.localeCompare(b.name))}
                isLoading={isLoadingCompaniesPilotageData}
              />
            </Row>
          </div>
        </Panel>
      </Collapse>
    </Form>
  );
}
